import React from 'react';
import { TFunction } from 'react-i18next';
import { useTranslation, Trans } from '../../i18n';

import CognitiveAbilitiesBarChart from '../../components/CognitiveAbilitiesBarChart/CognitiveAbilitiesBarChart';
import { MentalTestCategory, MentalTestScores } from '../../typings';
import { roundScore } from '../../utils';
import globalStyles from '../../styles/global.module.css';
import styles from './styles.module.css';

interface CognitiveAbilitiesExplanationProps {
    companyName: string;
    mentalTestScores: MentalTestScores | undefined;
}

export const renderCustomBarLabel = ({
    x,
    y,
    width,
    value,
}: {
    x: number;
    y: number;
    width: number;
    value: string;
}) => {
    return (
        <text
            x={x + width / 2}
            y={y}
            fill="#666"
            textAnchor="middle"
            dy={-6}>{`${value}%`}</text>
    );
};

export const mentalTestChartProps = (
    mentalTestCategories: MentalTestCategory[] | undefined,
    t: TFunction,
) => {
    const ordering = {
        ak: 1,
        lr: 2,
        nr: 3,
        vr: 4,
    };
    const mentalTestCategoryLabels = {
        ak: 'General knowledge',
        lr: 'Logical reasoning',
        nr: 'Numerical reasoning',
        vr: 'Verbal reasoning',
    };

    if (mentalTestCategories) {
        mentalTestCategories.sort((a, b) => {
            return ordering[a.id] - ordering[b.id];
        });

        return mentalTestCategories.map((category) => {
            const mentalTextCategoryTranslated =
                mentalTestCategoryLabels[category.id] === 'General knowledge'
                    ? `${t('COGNITIVE_ABILITIES_GRAPH.GENERAL_KNOWLEDGE')}`
                    : mentalTestCategoryLabels[category.id] ===
                      'Logical reasoning'
                    ? `${t('COGNITIVE_ABILITIES_GRAPH.LOGICAL_REASONING')}`
                    : mentalTestCategoryLabels[category.id] ===
                      'Numerical reasoning'
                    ? `${t('COGNITIVE_ABILITIES_GRAPH.NUMERICAL_REASONING')}`
                    : mentalTestCategoryLabels[category.id] ===
                      'Verbal reasoning'
                    ? `${t('COGNITIVE_ABILITIES_GRAPH.VERBAL_REASONING')}`
                    : null;

            if (category.company_baseline) {
                return {
                    name: mentalTextCategoryTranslated,
                    'Your score': roundScore(category.candidate_score),
                    'Company benchmark': roundScore(category.company_baseline),
                };
            } else {
                return {
                    name: mentalTextCategoryTranslated,
                    'Your score': roundScore(category.candidate_score),
                };
            }
        });
    }
};

export const isMentalTestBaselineAvailable = (
    chartProps: ReturnType<typeof mentalTestChartProps>,
) => {
    return (
        chartProps &&
        chartProps.some((mentalTest) => mentalTest['Company benchmark'])
    );
};

const CognitiveAbilitiesExplanation = (
    props: CognitiveAbilitiesExplanationProps,
) => {
    const { mentalTestScores, companyName } = props;
    const { t } = useTranslation();

    const categoriesScore = mentalTestScores && mentalTestScores.categories;
    const chartProps = mentalTestChartProps(categoriesScore, t);

    const isBaselineAvailable = isMentalTestBaselineAvailable(chartProps);

    return (
        <>
            <br></br>
            <h2
                className={`${globalStyles.textSubtitle} ${globalStyles.textNoAboveSpace}`}>
                {t('COGNITIVE_ABILITIES.SCORE_EXPLANATION.TITLE')}
            </h2>
            <p
                className={`${globalStyles.text} ${globalStyles.textNoAboveSpace}`}>
                {isBaselineAvailable ? (
                    <Trans
                        i18nKey="COGNITIVE_ABILITIES.SCORE_EXPLANATION.INTRO_TEXT"
                        components={{ italic: <i /> }}
                    />
                ) : (
                    <Trans
                        i18nKey="COGNITIVE_ABILITIES.SCORE_EXPLANATION.INTRO_TEXT_WHEN_NO_BASELINE"
                        components={{ italic: <i /> }}
                    />
                )}
            </p>
            <p
                className={`${globalStyles.text} ${globalStyles.textBold} ${globalStyles.textNoAboveSpace} ${globalStyles.textNoBelowSpace}`}>
                {t(
                    'COGNITIVE_ABILITIES.SCORE_EXPLANATION.WHY_WE_MEASURE_TITLE',
                )}
            </p>
            <p
                className={`${globalStyles.text} ${globalStyles.textNoAboveSpace} ${globalStyles.textNoBelowSpace}`}>
                {t(
                    'COGNITIVE_ABILITIES.SCORE_EXPLANATION.WHY_WE_MEASURE_PARAGRAPH',
                )}
            </p>
            {!mentalTestScores && (
                <p className={`${globalStyles.text}`}>
                    {t('COGNITIVE_ABILITIES.SCORE_EXPLANATION.NO_SCORE')}
                </p>
            )}
            {!!mentalTestScores && (
                <div className={`${styles.cognitiveAbilitiesBarChartWrap}`}>
                    <CognitiveAbilitiesBarChart
                        mentalTestScores={mentalTestScores}
                        companyName={companyName}
                    />
                </div>
            )}
        </>
    );
};

export default CognitiveAbilitiesExplanation;
